import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import {CardProps, BoxProps, withStyles} from '@material-ui/core';

import InfoTooltip from '../InfoTooltip';
import {getDirectionStyles} from '../../../utils/ThemeUtils';

interface TrendsMap<T> {
    positive: T;
    negative: T;
}

function getMappedTrends<T = any>(trend: number, map: TrendsMap<T>): T {
    return trend > 0 ? map.positive : map.negative;
}

const Rank = styled.span<{
    rank: number;
    trend: number;
    color?: string;
}>`
    width: ${p => p.rank}%;
    height: 8px;
    margin: 0 10px;

    background-color: ${p =>
        p.color
            ? p.color
            : getMappedTrends<string>(p.trend, {
                  positive: p.theme.muiTheme.palette.success.light,
                  negative: p.theme.muiTheme.palette.error.light
              })};
`;

const StyledCard = withStyles<string, undefined, CardProps>(theme => ({
    root: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '53px',
        minWidth: '300px',
        padding: theme.spacing(3),
        // @ts-ignore
        backgroundColor: theme.palette.background.overlays
    }
}))(Card);

const StyledLabel = withStyles<string, undefined, BoxProps>(theme => ({
    root: {
        display: 'flex',
        width: '30%',
        minWidth: '30%',

        margin: getDirectionStyles(theme.direction)(
            theme.spacing(0, 3, 0, 0),
            theme.spacing(0, 0, 0, 3)
        ),

        [theme.breakpoints.up('fmd')]: {
            width: '180px',
            minWidth: '180px'
        },
        [theme.breakpoints.up('flg')]: {
            width: '30%',
            minWidth: '30%'
        }
    }
}))(Box);

const StyledTypography = styled(Typography)`
    cursor: pointer;
`;

export interface MyTop10GamesStatisticsCardProps {
    rank: number;
    title: string;
    code: string;
    value?: number | string;
    trend?: number;
    bar?: number;
    tooltip?: JSX.Element | string;
    color?: string;
    children?: ReactNode;
}

const MyTop10GamesStatisticsCard: FC<MyTop10GamesStatisticsCardProps> = ({
    rank,
    title,
    value,
    code,
    trend,
    bar,
    tooltip,
    color,
    children
}: MyTop10GamesStatisticsCardProps) => {
    const itemColor = title && code ? 'textPrimary' : 'textSecondary';

    return (
        <Box mb="1px">
            <StyledCard>
                <StyledLabel>
                    <Typography variant="body2" color={itemColor}>{`${rank}.`}</Typography>
                    <Box mx={1} width="100%">
                        {title ? (
                            <InfoTooltip value={title} code={code}>
                                <StyledTypography noWrap variant="body2">
                                    {title}
                                </StyledTypography>
                            </InfoTooltip>
                        ) : (
                            <Typography color={itemColor}>{'--'}</Typography>
                        )}
                    </Box>
                    {tooltip}
                </StyledLabel>

                {!!value && (
                    <Box display="flex" width="100%" mx={3}>
                        <Rank rank={bar} trend={trend} color={color} />
                    </Box>
                )}
                <Box display="flex">
                    <Box display="flex" minWidth={50}>
                        <Typography variant="body2" color={itemColor}>
                            {value}
                        </Typography>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" ml={2} minWidth={50}>
                        {children}
                    </Box>
                </Box>
            </StyledCard>
        </Box>
    );
};

export default MyTop10GamesStatisticsCard;
